/**
 * DialerDemoExtensions.jsx — demo-only components for the Sales Dialer:
 *   1. ContactListEditor     — multi-contact (primary/secondary) with per-
 *                              contact phones & emails (labelled cell/office/
 *                              fax/work/personal, etc.).
 *   2. CampaignLearningPath  — shows ONLY learning path content that matches
 *                              the active campaign tag (e.g. CCM campaign →
 *                              CCM slides). No cross-campaign noise.
 *   3. DialerEmailComposer    — quick compose card. Auto-suggests recipient
 *                              from lead contacts. Attach resources +
 *                              individual learning path slides as PDF.
 *                              Mocks Microsoft 365 send.
 *   4. DialerTour            — step-by-step guided tour covering every major
 *                              card in Agent + Admin views. Spotlights
 *                              data-testid targets; Next/Back/Skip/Finish.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  X, Phone, Mail, Plus, Trash2, Send, Paperclip, FileText, BookOpen,
  ChevronRight, ChevronDown, Info, Sparkles, ShieldCheck, Search, ExternalLink,
  GraduationCap, Clock, Volume2, VolumeX, Loader2,
} from "lucide-react";
import axios from "axios";
import CardTip from "@/components/CardTip";
import { ContactAddressInline } from "@/components/LeadAddressBlock";
import ResourcePickerPanel, { AttachFromResourcesLink } from "@/components/ResourcePickerPanel";
import { OPT, useFieldOptions } from "@/lib/leadFieldOptions";

const PHONE_LABELS = OPT.phoneLabel;
const EMAIL_LABELS = OPT.emailLabel;

// ───────────────────────────────────────────────────────────────────
// ContactListEditor — multi-contact list inside a lead.
// lead.contacts = [{ id, name, title, phones:[{id,label,number,is_primary?}],
//                    emails:[{id,label,address,is_primary?}] }]
// ───────────────────────────────────────────────────────────────────
export function ContactListEditor({ contacts = [], onChange, compact = false }) {
  useFieldOptions();
  const emit = (next) => onChange?.(next);
  const newId = () => `c-${Math.random().toString(36).slice(2, 8)}`;
  // Iter 138 (ioannis@ehiehr.com bug d924efb7) — Ampliz contacts store the
  // provider name in `first_name` + `last_name`, leaving `name` null. The
  // row used to render "Unnamed contact" for every Ampliz lead even
  // though the data was right there. Coalesce here.
  const displayName = (c) => {
    if (!c) return "";
    if (typeof c.name === "string" && c.name.trim()) return c.name.trim();
    const fl = [c.first_name, c.last_name].filter((p) => typeof p === "string" && p.trim());
    if (fl.length) return fl.join(" ").trim();
    if (typeof c.full_name === "string" && c.full_name.trim()) return c.full_name.trim();
    return "";
  };

  // Iter 134 (issue cdc84774): each contact row now has a view/edit
  // mode that mirrors the EditableField UX on the lead-info-at-top.
  // - Rows with a name AND at least one phone or email default to VIEW.
  // - Empty/just-added rows default to EDIT so the user can fill in.
  // - Clicking the pencil enters edit; clicking Done exits.
  // Without this, users were saving phones/emails (onChange persists
  // every keystroke) but had no visual confirmation that the data
  // was saved — they thought the save had failed because the inputs
  // never collapsed.
  const hasIdentity = (c) =>
    !!displayName(c) &&
    ((c?.phones || []).some((p) => p?.number?.trim()) ||
      (c?.emails || []).some((e) => e?.address?.trim()));
  const [editingMap, setEditingMap] = useState({}); // { [contact.id]: bool }
  const isEditing = (c) => {
    if (editingMap[c.id] !== undefined) return editingMap[c.id];
    return !hasIdentity(c); // open new contacts; closed for filled ones
  };
  const setEditing = (id, val) => setEditingMap((m) => ({ ...m, [id]: val }));

  const addContact = () => {
    const id = newId();
    setEditing(id, true);
    emit([...(contacts || []), { id, name: "", title: "", phones: [{ id: newId(), label: "primary", number: "" }], emails: [{ id: newId(), label: "work", address: "" }] }]);
  };
  const removeContact = (idx) => emit(contacts.filter((_, i) => i !== idx));
  const updateContact = (idx, patch) => emit(contacts.map((c, i) => i === idx ? { ...c, ...patch } : c));

  const addPhone = (idx) => updateContact(idx, { phones: [...(contacts[idx].phones || []), { id: newId(), label: "cell", number: "" }] });
  const removePhone = (idx, pi) => updateContact(idx, { phones: contacts[idx].phones.filter((_, i) => i !== pi) });
  const updatePhone = (idx, pi, patch) => updateContact(idx, { phones: contacts[idx].phones.map((p, i) => i === pi ? { ...p, ...patch } : p) });

  const addEmail = (idx) => updateContact(idx, { emails: [...(contacts[idx].emails || []), { id: newId(), label: "work", address: "" }] });
  const removeEmail = (idx, ei) => updateContact(idx, { emails: contacts[idx].emails.filter((_, i) => i !== ei) });
  const updateEmail = (idx, ei, patch) => updateContact(idx, { emails: contacts[idx].emails.map((e, i) => i === ei ? { ...e, ...patch } : e) });

  const updateContactAddress = (idx, address) => updateContact(idx, { address });

  const I = { border: "0.5px solid #ddd", borderRadius: 6, padding: "6px 8px", fontSize: 11, fontFamily: "var(--font)" };

  return (
    <div data-testid="contact-list-editor" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {contacts.length === 0 && (
        <div data-testid="contact-list-empty" style={{ padding: "14px", border: "1px dashed #ddd", borderRadius: 8, textAlign: "center", color: "#888", fontSize: 11 }}>No contacts on file. Add one to get started.</div>
      )}
      {contacts.map((c, idx) => {
        const editing = isEditing(c);
        // View-mode summary line
        if (!editing) {
          const primaryPhone = (c.phones || []).find((p) => p?.number?.trim())?.number || "";
          const primaryEmail = (c.emails || []).find((e) => e?.address?.trim())?.address || "";
          const phoneCount = (c.phones || []).filter((p) => p?.number?.trim()).length;
          const emailCount = (c.emails || []).filter((e) => e?.address?.trim()).length;
          return (
            <div
              key={c.id || idx}
              data-testid={`contact-row-${idx}`}
              data-mode="view"
              className="card"
              style={{
                padding: 12, background: idx === 0 ? "#F7FAFF" : "#fff",
                borderLeft: idx === 0 ? "3px solid #2563eb" : "3px solid #eee",
                cursor: "pointer", transition: "box-shadow 120ms",
              }}
              onClick={() => setEditing(c.id, true)}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {idx === 0 && (
                  <span data-testid={`contact-primary-badge-${idx}`} style={{ fontSize: 9, fontWeight: 600, color: "#fff", background: "#2563eb", padding: "2px 8px", borderRadius: 10, letterSpacing: "0.3px" }}>PRIMARY</span>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 600, fontSize: 13, color: "#0f172a" }}>{displayName(c) || <span style={{ color: "#9ca3af", fontStyle: "italic", fontWeight: 400 }}>Unnamed contact</span>}</span>
                    {c.title && <span style={{ fontSize: 11, color: "#64748b" }}>· {c.title}</span>}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 4, fontSize: 11, color: "#475569" }}>
                    {primaryPhone && (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <Phone style={{ width: 10, height: 10 }} /> {primaryPhone}
                        {phoneCount > 1 && <span style={{ color: "#94a3b8" }}>+{phoneCount - 1}</span>}
                      </span>
                    )}
                    {primaryEmail && (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <Mail style={{ width: 10, height: 10 }} /> {primaryEmail}
                        {emailCount > 1 && <span style={{ color: "#94a3b8" }}>+{emailCount - 1}</span>}
                      </span>
                    )}
                    {!primaryPhone && !primaryEmail && (
                      <span style={{ color: "#9ca3af", fontStyle: "italic" }}>No phone / email yet — click to add</span>
                    )}
                  </div>
                </div>
                <button
                  data-testid={`contact-edit-${idx}`}
                  onClick={(e) => { e.stopPropagation(); setEditing(c.id, true); }}
                  className="btn"
                  style={{ fontSize: 10, padding: "4px 10px" }}
                  aria-label="Edit contact"
                >
                  Edit
                </button>
              </div>
            </div>
          );
        }
        return (
        <div key={c.id || idx} data-testid={`contact-row-${idx}`} data-mode="edit" className="card" style={{ padding: 12, background: idx === 0 ? "#F7FAFF" : "#fff", borderLeft: idx === 0 ? "3px solid #2563eb" : "3px solid #eee" }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
            {idx === 0 && (
              <span data-testid={`contact-primary-badge-${idx}`} style={{ fontSize: 9, fontWeight: 600, color: "#fff", background: "#2563eb", padding: "2px 8px", borderRadius: 10, letterSpacing: "0.3px" }}>PRIMARY</span>
            )}
            <input data-testid={`contact-name-${idx}`} placeholder="Contact name" value={c.name || displayName(c) || ""} onChange={e => updateContact(idx, { name: e.target.value })} style={{ ...I, flex: 1, minWidth: 0 }} />
            <input data-testid={`contact-title-${idx}`} placeholder="Title / role" value={c.title || ""} onChange={e => updateContact(idx, { title: e.target.value })} style={{ ...I, flex: 1, minWidth: 0 }} />
            {contacts.length > 1 && (
              <button data-testid={`contact-remove-${idx}`} onClick={() => removeContact(idx)} className="btn" style={{ fontSize: 10, padding: "4px 8px" }}>
                <Trash2 style={{ width: 10, height: 10 }} />
              </button>
            )}
          </div>
          {/* Phones */}
          <div style={{ marginBottom: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
              <Phone style={{ width: 10, height: 10, color: "#555" }} />
              <span style={{ fontSize: 10, fontWeight: 600, color: "#555" }}>Phones</span>
              <button data-testid={`contact-add-phone-${idx}`} onClick={() => addPhone(idx)} className="btn" style={{ fontSize: 9, padding: "2px 6px" }}>
                <Plus style={{ width: 9, height: 9 }} /> Add
              </button>
            </div>
            {(c.phones || []).map((p, pi) => (
              <div key={p.id || pi} style={{ display: "flex", gap: 6, marginBottom: 4 }}>
                <select data-testid={`phone-label-${idx}-${pi}`} value={p.label || "primary"} onChange={e => updatePhone(idx, pi, { label: e.target.value })} style={{ ...I, width: 100 }}>
                  {PHONE_LABELS.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
                <input data-testid={`phone-number-${idx}-${pi}`} placeholder="(555) 123-4567" value={p.number || ""} onChange={e => updatePhone(idx, pi, { number: e.target.value })} style={{ ...I, flex: 1, minWidth: 0 }} />
                {c.phones.length > 1 && (
                  <button data-testid={`phone-remove-${idx}-${pi}`} onClick={() => removePhone(idx, pi)} className="btn" style={{ fontSize: 10, padding: "3px 6px" }}><Trash2 style={{ width: 9, height: 9 }} /></button>
                )}
              </div>
            ))}
          </div>
          {/* Emails */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
              <Mail style={{ width: 10, height: 10, color: "#555" }} />
              <span style={{ fontSize: 10, fontWeight: 600, color: "#555" }}>Emails</span>
              <button data-testid={`contact-add-email-${idx}`} onClick={() => addEmail(idx)} className="btn" style={{ fontSize: 9, padding: "2px 6px" }}>
                <Plus style={{ width: 9, height: 9 }} /> Add
              </button>
            </div>
            {(c.emails || []).map((em, ei) => (
              <div key={em.id || ei} style={{ display: "flex", gap: 6, marginBottom: 4 }}>
                <select data-testid={`email-label-${idx}-${ei}`} value={em.label || "work"} onChange={e => updateEmail(idx, ei, { label: e.target.value })} style={{ ...I, width: 100 }}>
                  {EMAIL_LABELS.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
                <input data-testid={`email-address-${idx}-${ei}`} placeholder="name@example.com" value={em.address || ""} onChange={e => updateEmail(idx, ei, { address: e.target.value })} style={{ ...I, flex: 1, minWidth: 0 }} />
                {c.emails.length > 1 && (
                  <button data-testid={`email-remove-${idx}-${ei}`} onClick={() => removeEmail(idx, ei)} className="btn" style={{ fontSize: 10, padding: "3px 6px" }}><Trash2 style={{ width: 9, height: 9 }} /></button>
                )}
              </div>
            ))}
          </div>
          {/* Per-contact address (collapsed by default, single address only) */}
          <div data-testid={`contact-address-wrap-${idx}`}>
            <ContactAddressInline address={c.address || null} onChange={(addr) => updateContactAddress(idx, addr)} />
          </div>
          {/* Iter 134: explicit Done button so the row collapses to view-mode. */}
          <div style={{ marginTop: 10, paddingTop: 8, borderTop: "0.5px dashed #e5e7eb", display: "flex", justifyContent: "flex-end" }}>
            <button
              data-testid={`contact-done-${idx}`}
              onClick={() => setEditing(c.id, false)}
              className="btn btn-primary"
              style={{ fontSize: 11, padding: "5px 12px" }}
            >
              Done
            </button>
          </div>
        </div>
        );
      })}
      {!compact && (
        <button data-testid="contact-add-btn" onClick={addContact} className="btn" style={{ fontSize: 11, alignSelf: "flex-start" }}>
          <Plus style={{ width: 10, height: 10 }} /> Add contact
        </button>
      )}
    </div>
  );
}

// Flatten all emails across contacts. Used for the auto-suggest in the email
// composer + for a read-only summary row on the in-call panel.
export function getLeadEmails(lead) {
  const inline = (lead?.email ? [{ label: "primary", address: lead.email, name: lead.name, title: lead.title }] : []);
  const fromContacts = ((lead?.contacts || []).flatMap(c => (c.emails || []).map(em => ({
    label: em.label || "work", address: em.address, name: c.name, title: c.title,
  })))).filter(em => !!em.address);
  // Dedup by address (case-insensitive)
  const seen = new Set();
  return [...inline, ...fromContacts].filter(em => {
    const k = (em.address || "").toLowerCase();
    if (seen.has(k) || !k) return false;
    seen.add(k); return true;
  });
}

export function getLeadPhones(lead) {
  const inline = (lead?.phone ? [{ label: "primary", number: lead.phone, name: lead.name, title: lead.title }] : []);
  const fromContacts = ((lead?.contacts || []).flatMap(c => (c.phones || []).map(p => ({
    label: p.label || "primary", number: p.number, name: c.name, title: c.title,
  })))).filter(p => !!p.number);
  const seen = new Set();
  return [...inline, ...fromContacts].filter(p => {
    const k = (p.number || "").replace(/[^0-9]/g, "");
    if (seen.has(k) || !k) return false;
    seen.add(k); return true;
  });
}

// ───────────────────────────────────────────────────────────────────
// CampaignLearningPath — narrows a list of scripts/learning paths to those
// whose tag/category matches the current campaign. [Legacy — kept exported
// so any old consumers still render; new usage should prefer
// CampaignLearningPathV2 which binds to the backend LEARNING_COURSES.]
// ───────────────────────────────────────────────────────────────────
export function CampaignLearningPath({ campaign, allScripts = [], onAttachSlide }) {
  const matching = useMemo(() => {
    if (!campaign) return allScripts;
    const tags = new Set([campaign.type, ...(campaign.tags || []), campaign.category, campaign.name?.toLowerCase()].filter(Boolean).map(s => String(s).toLowerCase()));
    return allScripts.filter(s => {
      const stags = [s.category, s.learning_path, s.name, ...(s.tags || [])].filter(Boolean).map(v => String(v).toLowerCase());
      return stags.some(t => [...tags].some(ct => t.includes(ct) || ct.includes(t)));
    });
  }, [campaign, allScripts]);
  const list = matching.length > 0 ? matching : allScripts.slice(0, 1);

  return (
    <div data-testid="campaign-learning-path" className="card" style={{ padding: "14px 16px", marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <BookOpen style={{ width: 12, height: 12, color: "#7C3AED" }} />
        <div style={{ fontSize: 12, fontWeight: 600 }}>Learning path</div>
        <span style={{ fontSize: 10, color: "#888" }}>· filtered by <strong>{campaign?.name || "campaign"}</strong></span>
      </div>
      {list.length === 0 && (
        <div data-testid="learning-path-empty" style={{ fontSize: 11, color: "#888" }}>No learning path content for this campaign.</div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {list.map((s, i) => (
          <div key={s.id || i} data-testid={`learning-path-slide-${s.id || i}`} style={{ padding: "8px 12px", background: "#fafafa", borderRadius: 6, display: "flex", alignItems: "flex-start", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600 }}>{s.name || `Slide ${i + 1}`}</div>
              <div style={{ fontSize: 10, color: "#666", marginTop: 2 }}>{s.category || s.learning_path || "General"}</div>
              {s.body && <div style={{ fontSize: 11, color: "#444", marginTop: 4, lineHeight: 1.4 }}>{s.body.slice(0, 140)}{s.body.length > 140 ? "…" : ""}</div>}
            </div>
            {onAttachSlide && (
              <button data-testid={`attach-slide-${s.id || i}`} onClick={() => onAttachSlide(s)} className="btn" style={{ fontSize: 10 }}>
                <Paperclip style={{ width: 9, height: 9 }} /> Attach to email
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// DialerEmailComposer — quick-send email card with mocked Microsoft 365.
// ───────────────────────────────────────────────────────────────────
export function DialerEmailComposer({ lead, resources = [], learningSlides = [], onClose, onSend }) {
  const suggestions = getLeadEmails(lead || {});
  const firstEmail = suggestions[0]?.address || "";
  const [to, setTo] = useState(firstEmail);
  const [subject, setSubject] = useState(`Follow-up — ${lead?.practice || "your practice"}`);
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [resourcePickerOpen, setResourcePickerOpen] = useState(false);

  const attach = (item) => {
    setAttachments(prev => prev.find(a => a.id === item.id) ? prev : [...prev, item]);
  };
  const removeAttachment = (id) => setAttachments(prev => prev.filter(a => a.id !== id));

  const appendSlideBody = (slide) => {
    setBody(prev => (prev ? prev + "\n\n" : "") + `— ${slide.name} —\n${slide.body || ""}`);
  };

  const handleSend = async () => {
    if (!to || !subject) { return; }
    setSending(true);
    await new Promise(r => setTimeout(r, 700));
    setSending(false);
    onSend?.({ to, subject, body, attachments, provider: "microsoft365" });
  };

  const I = { border: "0.5px solid #ddd", borderRadius: 6, padding: "7px 10px", fontSize: 12, fontFamily: "var(--font)", width: "100%" };

  return (
    <div data-testid="dialer-email-composer" style={{ position: "fixed", right: 0, top: 0, width: 520, height: "100vh", background: "#fff", boxShadow: "-4px 0 16px rgba(0,0,0,0.12)", zIndex: 90, display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "14px 18px", borderBottom: "0.5px solid #eee", display: "flex", alignItems: "center", gap: 10 }}>
        <Mail style={{ width: 14, height: 14, color: "#2563eb" }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Compose email</div>
          <div style={{ fontSize: 10, color: "#888", display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: 50, background: "#1D9E75" }} /> Microsoft 365 (mock)
          </div>
        </div>
        <button data-testid="email-composer-close" onClick={onClose} className="btn" style={{ fontSize: 11 }}>
          <X style={{ width: 10, height: 10 }} /> Close
        </button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: 18, display: "flex", flexDirection: "column", gap: 12 }}>
        {/* To */}
        <div>
          <label style={{ fontSize: 10, fontWeight: 600, color: "#888" }}>To</label>
          <input data-testid="email-to-input" value={to} onChange={e => setTo(e.target.value)} placeholder="recipient@example.com" style={I} />
          {suggestions.length > 0 && (
            <div data-testid="email-to-suggestions" style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
              {suggestions.map((em, i) => (
                <button key={em.address + i} data-testid={`email-to-suggest-${i}`} onClick={() => setTo(em.address)} className="btn" style={{ fontSize: 9, padding: "3px 8px", background: to === em.address ? "#E6F1FB" : "#fff" }}>
                  {em.name ? `${em.name} · ` : ""}{em.label} · {em.address}
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <label style={{ fontSize: 10, fontWeight: 600, color: "#888" }}>Subject</label>
          <input data-testid="email-subject-input" value={subject} onChange={e => setSubject(e.target.value)} style={I} />
        </div>
        <div>
          <label style={{ fontSize: 10, fontWeight: 600, color: "#888" }}>Body</label>
          <textarea data-testid="email-body-input" value={body} onChange={e => setBody(e.target.value)} rows={8} placeholder="Write your message or paste content from the learning path…" style={{ ...I, fontFamily: "var(--font-mono, monospace)", fontSize: 11, resize: "vertical" }} />
        </div>
        {/* Attachments */}
        <div data-testid="email-attachments-section">
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            <Paperclip style={{ width: 11, height: 11, color: "#555" }} />
            <span style={{ fontSize: 11, fontWeight: 600, color: "#555" }}>Attachments ({attachments.length})</span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
            {attachments.map(a => (
              <span key={a.id} data-testid={`email-attachment-${a.id}`} style={{ padding: "3px 8px", borderRadius: 14, background: "#E6F1FB", color: "#1967D2", fontSize: 10, display: "inline-flex", gap: 4, alignItems: "center" }}>
                {a.kind === "slide" ? <BookOpen style={{ width: 9, height: 9 }} /> : <FileText style={{ width: 9, height: 9 }} />}
                {a.name}
                <button onClick={() => removeAttachment(a.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#1967D2" }}><X style={{ width: 9, height: 9 }} /></button>
              </span>
            ))}
          </div>

          {/* Attach documents */}
          {resources.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: "#888", marginBottom: 4 }}>Documents in Resources</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {resources.map(r => (
                  <button key={r.id} data-testid={`email-attach-resource-${r.id}`} onClick={() => attach({ id: r.id, name: r.name, kind: "document" })} className="btn" style={{ fontSize: 10 }}>
                    <FileText style={{ width: 9, height: 9 }} /> {r.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Attach slides */}
          {learningSlides.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: "#888", marginBottom: 4 }}>Slides from Learning Path</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {learningSlides.map(s => (
                  <div key={s.id} style={{ display: "flex", gap: 2 }}>
                    <button data-testid={`email-attach-slide-${s.id}`} onClick={() => attach({ id: `slide-${s.id}`, name: `${s.name}.pdf`, kind: "slide", source: s.id })} className="btn" style={{ fontSize: 10 }}>
                      <BookOpen style={{ width: 9, height: 9 }} /> {s.name}
                    </button>
                    <button data-testid={`email-paste-slide-${s.id}`} onClick={() => appendSlideBody(s)} className="btn" title="Paste into body" style={{ fontSize: 9, padding: "3px 6px" }}>paste</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{ padding: "12px 18px", borderTop: "0.5px solid #eee", display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ flex: 1, fontSize: 10, color: "#888" }}>
          Send via Microsoft 365 · integration mocked for demo
        </div>
        <AttachFromResourcesLink onClick={() => setResourcePickerOpen(true)} />
        <button data-testid="email-composer-cancel" onClick={onClose} className="btn" style={{ fontSize: 11 }}>Cancel</button>
        <button data-testid="email-composer-send" onClick={handleSend} disabled={sending || !to || !subject} className="btn btn-primary" style={{ fontSize: 11, opacity: sending || !to || !subject ? 0.5 : 1 }}>
          <Send style={{ width: 10, height: 10 }} /> {sending ? "Sending…" : "Send"}
        </button>
      </div>
      <ResourcePickerPanel
        open={resourcePickerOpen}
        onClose={() => setResourcePickerOpen(false)}
        onAttachFile={(f) => setAttachments((prev) => prev.find((a) => a.id === f.id) ? prev : [...prev, { id: f.id, name: f.name, kind: "document" }])}
        onInsertVideoLink={(v) => {
          setAttachments((prev) => prev.find((a) => a.id === v.id) ? prev : [...prev, { id: v.id, name: v.title, kind: "video" }]);
          setBody((b) => (b || "") + `\n\n▶ Watch: ${v.title}\n${v.url}\n`);
        }}
      />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// DialerTour — guided step-by-step tour for Sales Dialer.
//
// Each step has an optional `viewContext` (one of "agent-queue",
// "agent-call", "admin-campaigns", "admin-scripts", "admin-agents",
// "admin-queue_ops", "admin-analytics"). The host page receives an
// `onStepEnter(step, idx)` callback and routes its view state to match
// so the spotlight always points at a visible element.
// ───────────────────────────────────────────────────────────────────
export const DIALER_TOUR_STEPS = [
  { testId: "sales-dialer-page", viewContext: "agent-queue", title: "Welcome to the Sales Dialer", body: "The Sales Dialer is your command center for outbound calls. It combines your work queue, live call tools, and lead management into one focused screen." },
  { testId: "admin-agent-toggle", viewContext: "agent-queue", title: "Agent vs Admin view", body: "Toggle between Agent View (daily dialing) and Admin View (campaign setup, scripts, queue ops, analytics). Both draw from the same shared data." },
  { testId: "campaign-grouped-queue", viewContext: "agent-queue", title: "Campaigns-first work queue", body: "Leads are grouped under their campaign. Each campaign card shows total pending, hot/warm/cold counts, and status. Click to expand." },
  { testId: "mini-campaign-queue", viewContext: "agent-call", title: "Mini campaign queue (during calls)", body: "While on a call, the mini queue shows what's remaining in the same campaign and who's next — at a glance." },
  { testId: "power-dial-toggle", viewContext: "agent-call", title: "Power Dial", body: "When ON, the dialer auto-advances to the next lead in the same campaign the moment you submit a disposition. Toggle persists across refreshes." },
  { testId: "in-call-lead-panel", viewContext: "agent-call", title: "Live lead contact panel", body: "Always-visible contact card during a call. Every field is editable in real time — Name, Title, Phone, Email, Practice, Specialty, Providers." },
  { testId: "in-call-contacts-editor", viewContext: "agent-call", title: "Multi-contact editor", body: "Add or update additional contacts for the lead on the fly — primary and secondary contacts with typed phones (cell, office, fax, direct) and emails (work, billing, personal, assistant). Perfect for capturing a decision-maker mid-call." },
  { testId: "ai-auto-apply-always-on", viewContext: "agent-call", title: "AI auto-apply", body: "The AI listens to the transcript and silently updates lead fields as it hears key details. Auto-apply is always ON in demo mode." },
  { testId: "google-verify-btn", viewContext: "agent-call", title: "Google verification", body: "One-click Google Places verification confirms phone, address, and website. A badge appears once verified across all views." },
  { testId: "open-full-editor-btn", viewContext: "agent-call", title: "All-fields side drawer", body: "Quick access to every lead field (budget, timeline, decision makers, pain points, tags, stage, AI score). Edits sync everywhere in real time." },
  { testId: "campaign-learning-path", viewContext: "agent-call", title: "Campaign-specific learning path", body: "Only content matching the active campaign shows up here — no cross-campaign noise. Smart-search billing codes, eligibility, and program requirements; click a chapter to open it in Resources → Learning Paths." },
  { testId: "dialer-email-composer-launch", viewContext: "agent-call", title: "Email composer", body: "Quick-send emails via Microsoft 365. Auto-suggests recipients from the lead's contacts, and attaches any resource or learning-path slide with one click." },
  { testId: "admin-tab-campaigns", viewContext: "admin-campaigns", title: "Admin — Campaigns", body: "Create and manage campaigns. Each campaign has a type (outbound, renewal, inbound) and a target segment that feeds the work queue." },
  { testId: "admin-tab-scripts", viewContext: "admin-scripts", title: "Admin — Scripts & Learning Paths", body: "Curate call scripts, objection handling, and training slides. Tag each slide with the campaigns it applies to — that's how the Learning Path card filters in Agent View." },
  { testId: "admin-tab-agents", viewContext: "admin-agents", title: "Admin — Agents", body: "See who's active, their current campaign, call volume, and performance metrics. Feeds the Queue Ops round-robin." },
  { testId: "admin-tab-analytics", viewContext: "admin-analytics", title: "Admin — Analytics", body: "Conversion rates, talk time, disposition breakdowns, per-rep performance. Filter by campaign or time window." },
];

export function DialerTour({ steps = DIALER_TOUR_STEPS, onClose, onComplete, onStepEnter, apiBase, authToken, defaultVoice = "coral" }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [viewport, setViewport] = useState({ w: typeof window !== "undefined" ? window.innerWidth : 1200, h: typeof window !== "undefined" ? window.innerHeight : 800 });
  const [narrateOn, setNarrateOn] = useState(() => {
    try { return localStorage.getItem("dialer_tour_narrate") === "1"; } catch (_) { return false; }
  });
  const [narrateLoading, setNarrateLoading] = useState(false);
  const audioRef = React.useRef(null);
  // OP-07 fix (Feb 12 2026 PM-4) — measure ACTUAL tooltip height instead
  // of trusting the 210px estimate, which underestimated tall content
  // (long body + narration toggle) and caused the bottom edge to clip
  // off-viewport on the smaller mid-tour steps. Refs are re-measured on
  // step change + window resize via the existing rect-update effect.
  const tooltipRef = React.useRef(null);
  const [tipH, setTipH] = useState(220);
  const step = steps[stepIdx];

  // Let the host app swap views (queue → call → admin tab) before we
  // measure the target element. Call onStepEnter on every step change.
  useEffect(() => {
    if (!step) return;
    try { onStepEnter?.(step, stepIdx); } catch (_) {}
  }, [stepIdx]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Track the target element's bounding rect on each step + reflow.
  // Rect is stored in **viewport coords** (getBoundingClientRect gives that
  // directly). The overlay is `position: fixed`, so children positioned
  // absolutely are relative to the viewport — no scrollY offset needed.
  useEffect(() => {
    if (!step) return;
    const update = () => {
      const el = document.querySelector(`[data-testid="${step.testId}"]`);
      setViewport({ w: window.innerWidth, h: window.innerHeight });
      if (!el) { setRect(null); return; }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    // Scroll target into view *once* per step so the getBoundingClientRect
    // reads a stable position. Running scrollIntoView on every tick caused
    // the tooltip to chase the target across the viewport.
    const el = document.querySelector(`[data-testid="${step.testId}"]`);
    if (el) { try { el.scrollIntoView({ block: "center", behavior: "smooth" }); } catch (_) {} }
    // Give the smooth-scroll a moment, then latch the rect.
    const t0 = setTimeout(update, 320);
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    const t = setInterval(update, 500);
    // OP-07 — measure actual tooltip height on each step + content change.
    // The tooltip mounts on the first render with stale `tipH`, then this
    // effect's measurement updates it; we re-flow position by setting
    // state, which triggers another render with the correct clamp.
    const measure = () => {
      if (tooltipRef.current) {
        const h = tooltipRef.current.offsetHeight;
        if (h && Math.abs(h - tipH) > 4) setTipH(h);
      }
    };
    const tm0 = setTimeout(measure, 50);
    const tm1 = setTimeout(measure, 350);
    return () => { window.removeEventListener("resize", update); window.removeEventListener("scroll", update, true); clearInterval(t); clearTimeout(t0); clearTimeout(tm0); clearTimeout(tm1); };
  }, [step]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Voice narration via OpenAI TTS. Fetches an MP3 blob for the current
  // step whenever narrate is enabled, then plays it. Cancelling on step
  // change avoids overlapping audio.
  useEffect(() => {
    let cancelled = false;
    const stopCurrent = () => {
      try { if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ""; } } catch (_) {}
      audioRef.current = null;
    };
    stopCurrent();
    if (!narrateOn || !step || !apiBase) return () => { cancelled = true; stopCurrent(); };

    const text = `${step.title}. ${step.body}`;
    setNarrateLoading(true);
    axios.post(`${apiBase}/api/tour/tts`, { text, voice: defaultVoice, model: "tts-1" }, {
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      responseType: "blob",
    }).then(res => {
      if (cancelled) return;
      const url = URL.createObjectURL(res.data);
      const a = new Audio(url);
      a.onended = () => { try { URL.revokeObjectURL(url); } catch (_) {} };
      audioRef.current = a;
      a.play().catch(() => {/* autoplay blocked — user gesture required */});
    }).catch(() => {/* silent — narration is optional */})
      .finally(() => { if (!cancelled) setNarrateLoading(false); });

    return () => { cancelled = true; stopCurrent(); };
  }, [narrateOn, stepIdx, apiBase, authToken, defaultVoice]);  // eslint-disable-line react-hooks/exhaustive-deps

  const toggleNarrate = () => {
    setNarrateOn(v => {
      const next = !v;
      try { localStorage.setItem("dialer_tour_narrate", next ? "1" : "0"); } catch (_) {}
      return next;
    });
  };

  if (!step) return null;
  const finish = () => {
    try { localStorage.setItem("dialer_tour_completed", "1"); } catch (_) {}
    try { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } } catch (_) {}
    onComplete?.();
  };
  const next = () => (stepIdx < steps.length - 1 ? setStepIdx(stepIdx + 1) : finish());
  const back = () => setStepIdx(Math.max(0, stepIdx - 1));

  // Viewport-aware tooltip placement. Tooltip is a fixed ~340×~measured-h box.
  // Place below the target if it fits, else above; otherwise clamp into
  // the viewport so nothing is ever cut off. The 48px top safe-zone clears
  // the sticky DEMO MODE banner and any fixed page header — without this
  // the first/Welcome tour step was rendering with its title hidden behind
  // the orange banner (OP-07, recurring 6 sessions).
  const TIP_W = 340;
  const TIP_H = Math.max(tipH, 180);  // measured height, never below baseline
  const MARGIN = 12;
  const SAFE_TOP = 56;                 // demo banner (~24px) + breathing room
  const SAFE_BOTTOM = 12;
  let tipTop, tipLeft;
  if (!rect) {
    tipTop = Math.max(SAFE_TOP, viewport.h - TIP_H - SAFE_BOTTOM);
    tipLeft = MARGIN;
  } else {
    // Horizontal: try to align with target left, clamp inside viewport.
    tipLeft = Math.max(MARGIN, Math.min(viewport.w - TIP_W - MARGIN, rect.left));
    // Vertical: prefer below the target.
    const roomBelow = viewport.h - (rect.top + rect.height) - SAFE_BOTTOM;
    const roomAbove = rect.top - SAFE_TOP;
    if (roomBelow >= TIP_H + MARGIN) {
      tipTop = rect.top + rect.height + MARGIN;
    } else if (roomAbove >= TIP_H + MARGIN) {
      tipTop = rect.top - TIP_H - MARGIN;
    } else {
      // Neither fully fits — pin to bottom of viewport, clamped under
      // SAFE_TOP. With overflowY:auto on the tooltip, content scrolls
      // inside the clamped box.
      tipTop = Math.max(SAFE_TOP, viewport.h - TIP_H - SAFE_BOTTOM);
    }
  }
  const maxTipH = viewport.h - SAFE_TOP - SAFE_BOTTOM;

  return (
    <div data-testid="dialer-tour-overlay" style={{ position: "fixed", inset: 0, zIndex: 200, pointerEvents: "none" }}>
      {/* Dimmed backdrop */}
      <div style={{ position: "absolute", inset: 0, background: "rgba(15,24,40,0.45)", pointerEvents: "auto" }} onClick={onClose} />
      {/* Spotlight */}
      {rect && (
        <div data-testid="dialer-tour-spotlight" style={{ position: "absolute", top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12, borderRadius: 10, boxShadow: "0 0 0 4px #2563eb, 0 0 0 9999px rgba(15,24,40,0.55)", pointerEvents: "none", transition: "top 0.2s, left 0.2s, width 0.2s, height 0.2s" }} />
      )}
      {/* Tooltip */}
      <div ref={tooltipRef} data-testid="dialer-tour-tooltip" style={{ position: "absolute", top: tipTop, left: tipLeft, width: TIP_W, maxHeight: maxTipH, overflowY: "auto", background: "#fff", borderRadius: 10, padding: 16, boxShadow: "0 12px 32px rgba(0,0,0,0.25)", pointerEvents: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
          <Info style={{ width: 12, height: 12, color: "#2563eb" }} />
          <span style={{ fontSize: 10, fontWeight: 600, color: "#888" }}>Step {stepIdx + 1} of {steps.length}</span>
          <div style={{ flex: 1 }} />
          <button
            data-testid="dialer-tour-narrate-toggle"
            onClick={toggleNarrate}
            title={narrateOn ? "Mute voice narration" : "Narrate this tour"}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", borderRadius: 10, fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font)", border: "0.5px solid", borderColor: narrateOn ? "#2563eb" : "#ddd", background: narrateOn ? "#E6F1FB" : "#fff", color: narrateOn ? "#185FA5" : "#555" }}
          >
            {narrateLoading ? <Loader2 style={{ width: 10, height: 10, animation: "spin 1s linear infinite" }} /> : (narrateOn ? <Volume2 style={{ width: 10, height: 10 }} /> : <VolumeX style={{ width: 10, height: 10 }} />)}
            {narrateOn ? "Voice on" : "Narrate"}
          </button>
        </div>
        <div data-testid="dialer-tour-title" style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{step.title}</div>
        <div data-testid="dialer-tour-body" style={{ fontSize: 12, color: "#444", lineHeight: 1.5, marginBottom: 12 }}>{step.body}</div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button data-testid="dialer-tour-skip" onClick={onClose} className="btn" style={{ fontSize: 11 }}>Skip tour</button>
          <div style={{ flex: 1 }} />
          {stepIdx > 0 && (
            <button data-testid="dialer-tour-back" onClick={back} className="btn" style={{ fontSize: 11 }}>Back</button>
          )}
          <button data-testid="dialer-tour-next" onClick={next} className="btn btn-primary" style={{ fontSize: 11 }}>
            {stepIdx < steps.length - 1 ? "Next" : "Finish"} <ChevronRight style={{ width: 10, height: 10 }} />
          </button>
        </div>
      </div>
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────
// CampaignLearningPathV2 — pulls the course matching campaign.course_id
// from the backend `/api/learning-paths/{course_id}` endpoint and shows
// chapters with a smart search bar (scans chapter titles, subtitles, and
// section content — catches billing codes like "99490", "G0556",
// program requirements, eligibility, etc).
//
// Clicking a chapter deep-links to /resources?tab=learning&course=…&chapter=…
// so the Learning Paths tab (under Resources) opens right at that chapter.
// ───────────────────────────────────────────────────────────────────
const LP_ACCENT = {
  blue: "#0071E3", rose: "#E11D48", cyan: "#06B6D4", emerald: "#10B981", amber: "#F59E0B",
};
const LP_GRADIENT = {
  blue: "linear-gradient(135deg, #0A1628 0%, #1B365D 50%, #0071E3 100%)",
  rose: "linear-gradient(135deg, #4C0519 0%, #9F1239 50%, #F43F5E 100%)",
  cyan: "linear-gradient(135deg, #062C33 0%, #155E75 50%, #06B6D4 100%)",
  emerald: "linear-gradient(135deg, #022C22 0%, #064E3B 50%, #10B981 100%)",
  amber: "linear-gradient(135deg, #451A03 0%, #78350F 50%, #F59E0B 100%)",
};

export function CampaignLearningPathV2({ campaign, apiBase, authToken, onNavigateToResources, onAttachSlide, externalSearch, maxChapters, compact }) {
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const courseId = campaign?.course_id || "";
  // When a parent (Campaign Overview right-column search) drives the query,
  // prefer that; the internal input is hidden in `compact` mode.
  const effectiveQ = typeof externalSearch === "string" ? externalSearch : q;

  // Fetch the course whenever the campaign's course_id changes.
  useEffect(() => {
    let cancelled = false;
    if (!courseId || !apiBase) { setCourse(null); return; }
    setLoading(true); setErr("");
    axios.get(`${apiBase}/api/learning-paths/${encodeURIComponent(courseId)}`, {
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
    }).then(res => {
      if (cancelled) return;
      setCourse(res.data || null);
    }).catch(e => {
      if (cancelled) return;
      setErr(e?.response?.data?.detail || "Failed to load course");
      setCourse(null);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [courseId, apiBase, authToken]);

  // Smart search: scans chapter title/subtitle + every section title/content.
  // Returns { chapter, matchedSections: [{sectionTitle, snippet}] }.
  const results = useMemo(() => {
    if (!course) return [];
    const query = (effectiveQ || "").trim().toLowerCase();
    const chapters = course.chapters || [];
    if (!query) return chapters.map(ch => ({ chapter: ch, matchedSections: [] }));
    const hits = [];
    for (const ch of chapters) {
      const inTitle = (ch.title || "").toLowerCase().includes(query);
      const inSub = (ch.subtitle || "").toLowerCase().includes(query);
      const matchedSections = [];
      for (const sec of (ch.sections || [])) {
        const t = (sec.title || "").toLowerCase();
        const c = (sec.content || "").toLowerCase();
        if (t.includes(query) || c.includes(query)) {
          // Build a 140-char snippet around the first match
          const idx = c.indexOf(query);
          let snippet = "";
          if (idx !== -1) {
            const start = Math.max(0, idx - 50);
            const end = Math.min(c.length, idx + query.length + 90);
            snippet = (sec.content || "").slice(start, end);
            if (start > 0) snippet = "…" + snippet;
            if (end < c.length) snippet = snippet + "…";
          } else {
            snippet = (sec.content || "").slice(0, 140);
          }
          matchedSections.push({ sectionTitle: sec.title, snippet });
        }
      }
      if (inTitle || inSub || matchedSections.length) {
        hits.push({ chapter: ch, matchedSections });
      }
    }
    return hits;
  }, [course, effectiveQ]);

  const accent = LP_ACCENT[course?.color] || "#0071E3";
  const gradient = LP_GRADIENT[course?.color] || LP_GRADIENT.blue;

  const goToCourse = () => onNavigateToResources?.({ courseId, chapterId: null });
  const goToChapter = (chapterId) => onNavigateToResources?.({ courseId, chapterId });

  // COMPACT MODE — rendered inside the Campaign Overview right column.
  // No course header (the column already has its own label), no internal
  // search (the parent owns the search box), chapter rows are minimal.
  if (compact) {
    const visible = (results || []).slice(0, maxChapters || 4);
    const trimmedQuery = (effectiveQ || "").trim();
    return (
      <div data-testid="campaign-learning-path">
        {loading && <div data-testid="lp-loading" style={{ fontSize: 11, color: "#888" }}>Loading…</div>}
        {err && !loading && (
          <div data-testid="lp-error" style={{ fontSize: 11, color: "#A32D2D" }}>Could not load learning path.</div>
        )}
        {!loading && !err && course && results.length === 0 && trimmedQuery && (
          <div data-testid="lp-no-results" style={{ fontSize: 11, color: "#888", padding: "6px 0" }}>
            No matches for “{trimmedQuery}”.
          </div>
        )}
        {!loading && !err && course && visible.length > 0 && (
          <div data-testid="lp-chapter-list" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {visible.map(({ chapter, matchedSections }, idx) => (
              <div key={chapter.id || idx} data-testid={`lp-chapter-${chapter.id || idx}`} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", border: "0.5px solid #eee", borderRadius: 8, background: "#fff" }}>
                <div style={{ fontSize: 10, color: "#888", width: 34, flexShrink: 0 }}>CH {chapter.order || idx + 1}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: "#1B365D", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{chapter.title}</div>
                  {chapter.subtitle && (
                    <div style={{ fontSize: 10, color: "#888", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{chapter.subtitle}</div>
                  )}
                  {matchedSections.length > 0 && (
                    <div data-testid={`lp-matched-${chapter.id || idx}`} style={{ fontSize: 10, color: "#92400E", background: "#FFFBEA", borderRadius: 4, padding: "3px 5px", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                      <strong>{matchedSections[0].sectionTitle}</strong> — {matchedSections[0].snippet}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 10, color: "#aaa", flexShrink: 0 }}>{chapter.estimated_time || ""}</div>
                <button
                  data-testid={`lp-open-chapter-${chapter.id || idx}`}
                  onClick={() => goToChapter(chapter.id)}
                  style={{ fontSize: 11, color: "#185FA5", background: "none", border: "none", cursor: "pointer", padding: 0, flexShrink: 0, fontFamily: "var(--font)" }}
                  title="Open in Resources → Learning Paths">
                  Open →
                </button>
              </div>
            ))}
            {results.length > visible.length && (
              <button
                data-testid="lp-view-all-chapters"
                onClick={goToCourse}
                style={{ fontSize: 11, color: "#185FA5", background: "none", border: "none", cursor: "pointer", padding: "6px 0", textAlign: "center", fontFamily: "var(--font)" }}>
                View all {course.total_chapters || (course.chapters || []).length} chapters →
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="campaign-learning-path" className="card" style={{ padding: 0, marginBottom: 12, overflow: "hidden" }}>
      {/* Header with course accent */}
      <div style={{ padding: "14px 16px", background: gradient, color: "#fff", display: "flex", alignItems: "center", gap: 10 }}>
        <GraduationCap style={{ width: 16, height: 16 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.2px" }} data-testid="lp-course-title">
            {course ? course.title : (loading ? "Loading learning path…" : (err ? "Learning path unavailable" : "Learning path"))}
          </div>
          <div style={{ fontSize: 10, opacity: 0.85, marginTop: 2 }}>
            filtered by <strong>{campaign?.name || "campaign"}</strong>{course ? ` · ${course.total_chapters || (course.chapters || []).length} chapters` : ""}
          </div>
        </div>
        <div style={{ background: "rgba(255,255,255,0.15)", borderRadius: 999, padding: 2, display: "inline-flex" }}>
          <CardTip
            testId="lp-card-tip"
            title="Campaign learning path"
            items={[
              "Content is scoped to the active campaign's course (CCM / APCM / RPM / EMR Sales Toolkit)",
              "Smart search scans chapter titles, subtitles, and section content",
              "Try billing codes (99490, G0556, 99454), program names, or eligibility terms",
              "Open takes you straight to the Resources → Learning Paths reader",
              "Email attaches the chapter as a PDF to a new draft",
            ]}
          />
        </div>
        {course && (
          <button
            data-testid="lp-open-in-resources"
            onClick={goToCourse}
            style={{ fontSize: 10, background: "rgba(255,255,255,0.18)", color: "#fff", border: "0.5px solid rgba(255,255,255,0.3)", borderRadius: 6, padding: "5px 9px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4, fontFamily: "var(--font)" }}
            title="Open this course in Resources → Learning Paths"
          >
            Open in Resources <ExternalLink style={{ width: 9, height: 9 }} />
          </button>
        )}
      </div>

      {/* Smart search */}
      <div style={{ padding: "10px 14px 0 14px" }}>
        <div style={{ position: "relative" }}>
          <Search style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 12, height: 12, color: "#888" }} />
          <input
            data-testid="lp-smart-search"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search billing codes, eligibility, program requirements (e.g. 99490, G0556, RPM)…"
            style={{ width: "100%", padding: "7px 10px 7px 30px", borderRadius: 8, border: "0.5px solid #ddd", fontSize: 11, fontFamily: "var(--font)" }}
          />
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: "10px 14px 14px 14px" }}>
        {loading && (
          <div data-testid="lp-loading" style={{ fontSize: 11, color: "#888", padding: "10px 0" }}>Loading learning path…</div>
        )}
        {err && !loading && (
          <div data-testid="lp-error" style={{ fontSize: 11, color: "#A32D2D", padding: "10px 0" }}>
            Could not load the learning path. Try opening Resources → Learning Paths directly.
          </div>
        )}
        {!loading && !err && course && results.length === 0 && (
          <div data-testid="lp-no-results" style={{ fontSize: 11, color: "#888", padding: "14px 0", textAlign: "center" }}>
            No chapters or sections match “{q}”.
          </div>
        )}
        {!loading && !err && course && results.length > 0 && (
          <div data-testid="lp-chapter-list" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {results.map(({ chapter, matchedSections }, idx) => (
              <div key={chapter.id || idx} data-testid={`lp-chapter-${chapter.id || idx}`} style={{ border: "0.5px solid #eee", borderRadius: 8, padding: "10px 12px", background: "#fff" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <div style={{ width: 4, alignSelf: "stretch", borderRadius: 2, background: accent }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.3px", color: "#888", textTransform: "uppercase" }}>Ch {chapter.order || idx + 1}</span>
                      <Clock style={{ width: 9, height: 9, color: "#aaa" }} />
                      <span style={{ fontSize: 9, color: "#aaa" }}>{chapter.estimated_time || ""}</span>
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#1B365D", marginTop: 2 }}>{chapter.title}</div>
                    {chapter.subtitle && (
                      <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>{chapter.subtitle}</div>
                    )}
                    {matchedSections.length > 0 && (
                      <div data-testid={`lp-matched-${chapter.id || idx}`} style={{ marginTop: 6, background: "#FFFBEA", border: "0.5px solid #FCD34D", borderRadius: 6, padding: "6px 8px" }}>
                        {matchedSections.slice(0, 3).map((m, mi) => (
                          <div key={mi} style={{ fontSize: 10, color: "#92400E", marginBottom: mi < Math.min(matchedSections.length, 3) - 1 ? 4 : 0 }}>
                            <strong>{m.sectionTitle}</strong> — {m.snippet}
                          </div>
                        ))}
                        {matchedSections.length > 3 && (
                          <div style={{ fontSize: 9, color: "#92400E", fontStyle: "italic", marginTop: 3 }}>+{matchedSections.length - 3} more matches</div>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
                    <button
                      data-testid={`lp-open-chapter-${chapter.id || idx}`}
                      onClick={() => goToChapter(chapter.id)}
                      className="btn"
                      style={{ fontSize: 10, padding: "4px 8px" }}
                      title="Open this chapter in Resources → Learning Paths"
                    >
                      Open <ChevronRight style={{ width: 9, height: 9 }} />
                    </button>
                    {onAttachSlide && (
                      <button
                        data-testid={`lp-attach-chapter-${chapter.id || idx}`}
                        onClick={() => onAttachSlide({
                          id: `${courseId}-${chapter.id}`,
                          name: `${chapter.title}.pdf`,
                          body: (chapter.sections || []).map(s => `— ${s.title} —\n${s.content || ""}`).join("\n\n"),
                          kind: "chapter",
                        })}
                        className="btn"
                        style={{ fontSize: 10, padding: "4px 8px" }}
                        title="Attach this chapter as a PDF to a new email"
                      >
                        <Paperclip style={{ width: 9, height: 9 }} /> Email
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
